"use client";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import { useImport } from "@/context/importContext";
import { useSession } from "@/context/SessionContext";
import { useProcessImportRows } from "@/utils/hooks/import/importProcessingHooks";
import {
  autoMapColumns,
  buildProfileRows,
  duplicateTargets,
  missingRequiredFields,
} from "@/utils/import/importSource";
import { useMemo, useState } from "react";
import { LuArrowLeft, LuCircleCheck, LuTriangleAlert } from "react-icons/lu";
import { useResolvedSource } from "./useResolvedSource";

const IGNORE = "ignore";
const VISIBLE_ROWS = 50;

const confidenceBadge = (confidence: number) => {
  if (confidence >= 100) return { label: "Alta", color: "success" as const };
  if (confidence >= 80) return { label: "Media", color: "warning" as const };
  if (confidence > 0) return { label: "Baja", color: "warning" as const };
  return { label: "Sin asignar", color: "neutral" as const };
};

/**
 * Step "columns": every column of the data is matched to a field of the profile.
 * The data is shown as it is, with the field of each column chosen in its header, so
 * the user decides looking at the values. The match is suggested from the names (or
 * the position when there is no header); what is left unmatched is ignored.
 */
export default function ColumnMapping() {
  const { importData, setImportData } = useImport();
  const { selectedProfile, source, fileErrors } = importData;
  const { session } = useSession();
  const { languageCode } = session;
  const { fields, hasHeader, resolved } = useResolvedSource();
  const processRows = useProcessImportRows();
  const [onlyAssigned, setOnlyAssigned] = useState(false);

  const { headers, dataRows, columnCount, fromTemplate } = resolved;

  const suggestion = useMemo(
    () => autoMapColumns(headers, columnCount, fields),
    [headers, columnCount, fields],
  );

  const targets: Array<string | null> =
    source.mapping && source.mapping.length === columnCount
      ? source.mapping
      : suggestion.map((match) => match.target);

  const fieldLabel = (key: string) => {
    const column = selectedProfile.columns[key];
    return column?.fieldHeader?.[languageCode] || column?.fieldHeader?.en || key;
  };

  const duplicated = duplicateTargets(targets);
  const missing = missingRequiredFields(targets, fields);
  const canContinue = duplicated.length === 0 && missing.length === 0 && dataRows.length > 0;
  const isCustomized = source.mapping !== null;

  const setTarget = (columnIndex: number, target: string | null) => {
    const next = [...targets];
    next[columnIndex] = target;
    setImportData((prev) => ({ ...prev, source: { ...prev.source, mapping: next } }));
  };

  const handleContinue = () => {
    void processRows(buildProfileRows(dataRows, targets, fields));
  };

  const assignedKeys = new Set(targets.filter((target): target is string => target !== null));
  // At most a few dozen columns by 50 rows: cheap enough to draw on every change.
  const visibleColumns = Array.from({ length: columnCount }, (_, index) => index).filter(
    (index) => !onlyAssigned || targets[index] !== null,
  );
  const hiddenCount = columnCount - visibleColumns.length;
  const shownRows = dataRows.slice(0, VISIBLE_ROWS);

  return (
    <div className="space-y-4">
      {fromTemplate && missing.length === 0 && duplicated.length === 0 ? (
        <div className="flex items-center gap-2 rounded-md border border-su bg-su-3 p-3 text-sm text-su+2">
          <LuCircleCheck className="h-4 w-4 shrink-0" />
          Se reconoció la plantilla de Orquesti: las columnas ya están asignadas. Revísalas si lo necesitas.
        </div>
      ) : fromTemplate ? (
        <p className="text-sm text-fg-muted">
          Se reconoció la plantilla de Orquesti, pero el cambio de asignación necesita revisión.
        </p>
      ) : (
        <p className="text-sm text-fg-muted">
          Elige sobre cada columna a qué campo corresponde. Las columnas que dejes en
          &quot;Ignorar&quot; no se importan.
          {!hasHeader && " Tus datos no tienen encabezado: se asignaron por su posición."}
        </p>
      )}

      {missing.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-de bg-de-4 p-3 text-sm text-de">
          <LuTriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            Faltan columnas obligatorias: {missing.map((field) => fieldLabel(field.key)).join(", ")}.
            Asígnalas a una columna de tus datos.
          </p>
        </div>
      )}
      {duplicated.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-de bg-de-4 p-3 text-sm text-de">
          <LuTriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            Hay campos asignados a más de una columna: {duplicated.map((key) => fieldLabel(key)).join(", ")}.
          </p>
        </div>
      )}
      {missing.length === 0 && duplicated.length === 0 && !fromTemplate && (
        <div className="flex items-center gap-2 rounded-md border border-su bg-su-3 p-3 text-sm text-su+2">
          <LuCircleCheck className="h-4 w-4 shrink-0" />
          Todas las columnas obligatorias están asignadas.
        </div>
      )}

      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Switch id="import-only-assigned" checked={onlyAssigned} onCheckedChange={setOnlyAssigned} />
          <Label htmlFor="import-only-assigned" className="text-sm">
            Mostrar solo las columnas asignadas
            {onlyAssigned && hiddenCount > 0 ? ` (${hiddenCount} ocultas)` : ""}
          </Label>
        </div>
        <span className="text-xs text-fg-muted">
          {dataRows.length > VISIBLE_ROWS
            ? `Se muestran las primeras ${VISIBLE_ROWS} de ${dataRows.length} filas; la asignación vale para todas`
            : `${dataRows.length} ${dataRows.length === 1 ? "fila" : "filas"}`}
        </span>
      </div>

      <Card className="border-bg+2">
        <CardContent className="p-0">
          <div className="max-h-[calc(100dvh-470px)] min-h-56 overflow-auto">
            <table className="w-full border-separate border-spacing-0 text-sm">
              <thead className="sticky top-0 z-10 bg-bg+1">
                <tr>
                  <th className="sticky left-0 z-20 bg-bg+1 px-3 py-2 text-left text-xs font-medium text-fg-muted">
                    #
                  </th>
                  {visibleColumns.map((index) => {
                    const target = targets[index] ?? null;
                    const suggested = suggestion[index];
                    const isManual = target !== null && target !== suggested?.target;
                    const badge = isManual
                      ? { label: "Elegida por ti", color: "neutral" as const }
                      : confidenceBadge(target ? (suggested?.confidence ?? 0) : 0);
                    const isDuplicate = target !== null && duplicated.includes(target);

                    return (
                      <th key={index} className="min-w-52 px-2 py-2 text-left align-top font-normal">
                        <Select
                          value={target ?? IGNORE}
                          onValueChange={(value) => setTarget(index, value === IGNORE ? null : value)}
                        >
                          <SelectTrigger
                            aria-label={`Asignar la columna ${headers?.[index] || index + 1}`}
                            variant={isDuplicate ? "destructive" : "default"}
                            className={
                              isDuplicate
                                ? ""
                                : target === null
                                  ? "text-fg-muted"
                                  : "border-su"
                            }
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={IGNORE}>Ignorar</SelectItem>
                            {fields.map((field) => (
                              <SelectItem key={field.key} value={field.key}>
                                {fieldLabel(field.key)}
                                {field.required ? " *" : ""}
                                {assignedKeys.has(field.key) && field.key !== target ? " (ya asignado)" : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <div className="mt-1 flex items-center justify-between gap-2">
                          <span
                            className="max-w-36 truncate text-xs font-medium text-fg"
                            title={headers?.[index] || `Columna ${index + 1}`}
                          >
                            {headers?.[index] || `Columna ${index + 1}`}
                          </span>
                          <Badge tone="soft" color={badge.color} size="xs">
                            {badge.label}
                          </Badge>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {shownRows.map((row, rowIndex) => (
                  <tr key={rowIndex} className="border-t border-bg+2">
                    <td className="sticky left-0 z-[1] bg-bg px-3 py-2 font-mono text-xs text-fg-muted">
                      {rowIndex + 1}
                    </td>
                    {visibleColumns.map((columnIndex) => {
                      const value = row[columnIndex] ?? "";
                      return (
                        <td
                          key={columnIndex}
                          className={`max-w-52 truncate whitespace-nowrap px-3 py-2 text-xs ${
                            targets[columnIndex] === null ? "text-fg-muted opacity-50" : "text-fg"
                          }`}
                          title={value.length > 30 ? value : undefined}
                        >
                          {value}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {fileErrors.length > 0 && (
        <div className="rounded-md border border-de bg-de-4 p-3 text-sm text-de">
          {fileErrors.map((fileError, index) => (
            <p key={index}>{fileError.message}</p>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() =>
              setImportData((prev) => ({ ...prev, fileErrors: [], currentStep: "upload" }))
            }
          >
            <LuArrowLeft className="mr-1 h-4 w-4" />
            Volver a los datos
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!isCustomized}
            onClick={() => setImportData((prev) => ({ ...prev, source: { ...prev.source, mapping: null } }))}
          >
            Usar la asignación automática
          </Button>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-fg-muted">
            {dataRows.length} {dataRows.length === 1 ? "fila" : "filas"} a validar
          </span>
          <Button onClick={handleContinue} disabled={!canContinue || source.isProcessing}>
            {source.isProcessing ? (
              <>
                <Spinner />
                Validando...
              </>
            ) : (
              "Continuar"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
